/** @jsxImportSource theme-ui */
import { Badge, Flex, Link } from 'theme-ui';
import { PencilIcon, TrashIcon } from '@heroicons/react/solid';
import { format } from 'date-fns';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useDeleteTicketMutation } from '@/store/apiService';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Ticket } from '@/types/types';

const formatDate = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : format(d, 'd MMM yyyy, p');
};

const DeleteCell = ({ row }: { row: { original: Ticket } }) => {
    const ticket = row.original;
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTicket, deleteState] = useDeleteTicketMutation();

    return (
        <Flex sx={{ justifyContent: 'flex-end', flexGrow: 1, alignItems: 'center', gap: 2 }}>
            {confirmOpen ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={deleteState.isLoading}
                    headingText={`Delete ticket #${ticket.Id}`}
                    content="This will permanently delete the ticket and all its comments and attachments. Are you sure?"
                    onNo={() => setConfirmOpen(false)}
                    onYes={() => {
                        if (ticket.Id == null) return;
                        deleteTicket({ Id: ticket.Id })
                            .unwrap()
                            .then(() => setConfirmOpen(false))
                            .catch((err) => {
                                setConfirmOpen(false);
                                toast.error(err?.data ?? err?.message ?? 'Delete failed', {
                                    autoClose: 5000,
                                });
                            });
                    }}
                />
            ) : null}
            <Link variant="button" href={`/#/tickets/${ticket.Id}`} title="Edit">
                <PencilIcon width={16} height={16} />
            </Link>
            <Button
                variant="basicDanger"
                isLoading={deleteState.isLoading}
                onClick={() => setConfirmOpen(true)}
            >
                <TrashIcon width={16} height={16} />
            </Button>
        </Flex>
    );
};

export const ticketColumns = [
    {
        columnKey: 'Id',
        Header: '#',
        accessor: 'Id',
        width: '60px',
    },
    {
        columnKey: 'Title',
        Header: 'Title',
        accessor: 'Title',
        width: '25%',
    },
    {
        columnKey: 'CaseStatus',
        Header: 'Status',
        accessor: 'CaseStatus',
        Cell: ({ cell }: any) => {
            const status = cell.value as Ticket['CaseStatus'];
            if (!status) return null;
            return (
                <Badge sx={{ backgroundColor: status.Color || 'primary' }}>{status.Name}</Badge>
            );
        },
    },
    {
        columnKey: 'CaseType',
        Header: 'Type',
        accessor: 'CaseType',
        Cell: ({ cell }: any) => <>{(cell.value as Ticket['CaseType'])?.Name ?? ''}</>,
    },
    {
        columnKey: 'CaseArea',
        Header: 'Area',
        accessor: 'CaseArea',
        Cell: ({ cell }: any) => <>{(cell.value as Ticket['CaseArea'])?.Name ?? ''}</>,
    },
    {
        columnKey: 'CaseOwner',
        Header: 'Owner',
        accessor: 'CaseOwner',
    },
    {
        columnKey: 'Created',
        Header: 'Created',
        accessor: 'Created',
        Cell: ({ cell }: any) => <>{formatDate(cell.value)}</>,
    },
    {
        columnKey: 'Actions',
        accessor: 'Actions',
        Header: '',
        Cell: DeleteCell,
    },
];
